import React, { useState, useEffect } from 'react';
import { Plus, Calendar, DollarSign, CheckCircle, Clock, XCircle, AlertCircle } from 'lucide-react';
import { forecastService, ProjectMilestone } from '../../lib/forecastService';
import { useToast } from '../../contexts/ToastContext';

interface MilestoneManagerProps {
  projectId: string;
  onUpdate: () => void;
}

const MilestoneManager: React.FC<MilestoneManagerProps> = ({ projectId, onUpdate }) => {
  const { showToast } = useToast();
  const [milestones, setMilestones] = useState<ProjectMilestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    milestone_name: '',
    percentage_of_total: '',
    amount: '',
    estimated_payment_date: '',
  });

  useEffect(() => {
    loadMilestones();
  }, [projectId]);

  const loadMilestones = async () => {
    try {
      setLoading(true);
      const data = await forecastService.getMilestones(projectId);
      setMilestones(data);
    } catch (error) {
      showToast('Failed to load milestones', 'error');
      console.error('Error loading milestones:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await forecastService.createMilestone({
        project_id: projectId,
        milestone_name: formData.milestone_name,
        percentage_of_total: parseFloat(formData.percentage_of_total),
        amount: parseFloat(formData.amount),
        estimated_payment_date: formData.estimated_payment_date,
        status: 'pending',
        payment_received: false,
      });
      showToast('Milestone created successfully', 'success');
      setFormData({
        milestone_name: '',
        percentage_of_total: '',
        amount: '',
        estimated_payment_date: '',
      });
      setShowForm(false);
      loadMilestones();
      onUpdate();
    } catch (error) {
      showToast('Failed to create milestone', 'error');
      console.error('Error creating milestone:', error);
    }
  };

  const handleMarkPaid = async (milestoneId: string) => {
    try {
      await forecastService.updateMilestone(milestoneId, {
        status: 'paid',
        payment_received: true,
        actual_payment_date: new Date().toISOString().split('T')[0],
      });
      showToast('Milestone marked as paid', 'success');
      loadMilestones();
      onUpdate();
    } catch (error) {
      showToast('Failed to update milestone', 'error');
      console.error('Error updating milestone:', error);
    }
  };

  const handleUpdateDate = async (milestoneId: string) => {
    const newDate = prompt('Enter new estimated payment date (YYYY-MM-DD):');
    if (!newDate) return;

    try {
      await forecastService.updateMilestone(milestoneId, {
        estimated_payment_date: newDate,
      });
      showToast('Milestone date updated', 'success');
      loadMilestones();
      onUpdate();
    } catch (error) {
      showToast('Failed to update milestone', 'error');
      console.error('Error updating milestone:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-blue-600" />;
      case 'overdue':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-gray-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-blue-100 text-blue-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <div className="text-center py-4 text-gray-500">Loading milestones...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-gray-900">Payment Milestones</h4>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Milestone
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Milestone Name *
              </label>
              <input
                type="text"
                required
                value={formData.milestone_name}
                onChange={(e) => setFormData({ ...formData, milestone_name: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Deposit, Design Approval, Final Payment, etc."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Percentage *
              </label>
              <div className="relative">
                <input
                  type="number"
                  required
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.percentage_of_total}
                  onChange={(e) => setFormData({ ...formData, percentage_of_total: e.target.value })}
                  className="w-full px-3 py-2 pr-8 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="30"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="3000.00"
                />
              </div>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estimated Payment Date *
              </label>
              <input
                type="date"
                required
                value={formData.estimated_payment_date}
                onChange={(e) => setFormData({ ...formData, estimated_payment_date: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-3 py-1 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add Milestone
            </button>
          </div>
        </form>
      )}

      {milestones.length === 0 ? (
        <div className="text-center py-8 bg-white border border-gray-200 rounded-lg">
          <Calendar className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600">No milestones yet</p>
          <p className="text-xs text-gray-500 mt-1">Add milestones to track project payments</p>
        </div>
      ) : (
        <div className="space-y-2">
          {milestones.map((milestone) => (
            <div
              key={milestone.id}
              className="bg-white border border-gray-200 rounded-lg p-3"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 text-sm">{milestone.milestone_name}</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(milestone.status)}`}>
                      {getStatusIcon(milestone.status)}
                      {milestone.status.charAt(0).toUpperCase() + milestone.status.slice(1)}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-xs text-gray-600">
                    <div className="flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />
                      ${milestone.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      <span className="text-gray-500">({milestone.percentage_of_total}%)</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {milestone.actual_payment_date ? (
                        <span className="text-green-600">
                          Paid: {new Date(milestone.actual_payment_date).toLocaleDateString()}
                        </span>
                      ) : (
                        <span>
                          Est: {new Date(milestone.estimated_payment_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {milestone.status === 'pending' && (
                  <div className="flex items-center gap-2 ml-2">
                    <button
                      onClick={() => handleUpdateDate(milestone.id)}
                      className="px-2 py-1 text-xs text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
                    >
                      Update Date
                    </button>
                    <button
                      onClick={() => handleMarkPaid(milestone.id)}
                      className="px-2 py-1 text-xs text-green-600 bg-green-50 rounded hover:bg-green-100 transition-colors"
                    >
                      Mark Paid
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MilestoneManager;